/**
 * Built-in browser tools for agent RPC calls.
 *
 * Five granular built-ins (the agent sees each `browser_`-prefixed; the backend
 * adds the prefix when registering with the toolkit):
 *
 *   - confirmAction(why, message)            — yes/no dialog (non-read-only).
 *   - promptUser(why, message, default?)     — single short string (non-read-only).
 *   - getClipboard(why)                      — read clipboard text (read-only).
 *   - getBrowserInfo(why)                    — userAgent/viewport/url/etc (read-only).
 *   - getSelection(why)                      — current page text selection (read-only).
 *
 * Canvas tools come from createCanvasToolDefinitions().
 *
 * For guidance on adding new browser tools, see
 * `docs/browser-tools/developer-guide.md`.
 */

import { BrowserRpcMethods, type RegisteredTool } from './BrowserRpcMethods';
import { createCanvasToolDefinitions } from './CanvasTools';
import { RpcErrorCodes } from './types';

const whyParam = {
  type: 'string',
  description:
    'Why you are calling this tool — one short sentence explaining your intent. Required on every call for audit and human review.',
};

/**
 * Create and register built-in browser tools.
 *
 * @returns BrowserRpcMethods instance with registered tools
 */
export function createBrowserTools(): BrowserRpcMethods {
  const methods = new BrowserRpcMethods();

  const okStatus = { type: 'object', properties: { status: { type: 'string' }, hint: { type: 'string' } }, required: ['status'] };

  // ── confirmAction — yes/no dialog (agent sees "browser_confirmAction") ──
  methods.registerTool({
    name: 'confirmAction',
    category: 'user-interaction',
    disclosureGroup: 'browser_interaction',
    description:
      'Show the user a native yes/no confirmation dialog. Returns { confirmed, message, hint }. ' +
      'Use only for consequential actions (deletions, sends, purchases).',
    annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: false },
    outputSchema: okStatus,
    parameters: {
      type: 'object',
      properties: {
        why: whyParam,
        message: { type: 'string', description: 'Text shown to the user verbatim.' },
      },
      required: ['why', 'message'],
    },
    handler: async (params) => {
      const message = params.message as string;
      if (!message) {
        return { status: 'error', code: 'missing_param', message: 'message is required.', hint: 'Pass a clear yes/no question.' };
      }
      const confirmed = window.confirm(message);
      return {
        status: 'ok',
        confirmed,
        message,
        hint: confirmed
          ? 'User confirmed — proceed with the action you were gating on.'
          : 'User declined — do NOT proceed; tell them you heard and offer an alternative.',
      };
    },
  });

  // ── promptUser — single-string prompt (agent sees "browser_promptUser") ──
  methods.registerTool({
    name: 'promptUser',
    category: 'user-interaction',
    disclosureGroup: 'browser_interaction',
    description:
      'Collect a single short string from the user via a native prompt dialog. ' +
      'Returns { cancelled, value, message, hint }. For multi-field input use render_form instead.',
    annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: false },
    outputSchema: okStatus,
    parameters: {
      type: 'object',
      properties: {
        why: whyParam,
        message: { type: 'string', description: 'Text shown to the user verbatim.' },
        default: { type: 'string', description: 'Optional pre-filled default value.' },
      },
      required: ['why', 'message'],
    },
    handler: async (params) => {
      const message = params.message as string;
      if (!message) {
        return { status: 'error', code: 'missing_param', message: 'message is required.', hint: 'Pass a clear question.' };
      }
      const value = window.prompt(message, params.default as string | undefined);
      const cancelled = value === null;
      return {
        status: 'ok',
        cancelled,
        value,
        message,
        hint: cancelled
          ? 'User cancelled — do NOT assume any value; ask again or try another approach.'
          : 'User supplied a value — use it.',
      };
    },
  });

  // ── getClipboard — read clipboard text (agent sees "browser_getClipboard") ──
  methods.registerTool({
    name: 'getClipboard',
    category: 'browser-capability',
    disclosureGroup: 'browser',
    description:
      'Read text from the user clipboard (requires one-time permission; the browser may prompt). ' +
      'Returns { text, length, hint } or a PERMISSION_DENIED RPC error.',
    annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: true },
    outputSchema: okStatus,
    parameters: { type: 'object', properties: { why: whyParam }, required: ['why'] },
    requiresPermission: ['clipboard-read'],
    handler: async () => {
      try {
        const text = await navigator.clipboard.readText();
        return {
          status: 'ok',
          text,
          length: text.length,
          hint: text.length === 0 ? 'Clipboard was empty.' : 'Clipboard contents available in `text`.',
        };
      } catch {
        throw {
          code: RpcErrorCodes.PERMISSION_DENIED,
          message: 'Clipboard permission denied or not available',
          data: { permission: 'clipboard-read' },
        };
      }
    },
  });

  // ── getBrowserInfo — environment (agent sees "browser_getBrowserInfo") ──
  methods.registerTool({
    name: 'getBrowserInfo',
    category: 'browser-capability',
    disclosureGroup: 'browser',
    description:
      'Read the browser environment: userAgent, viewport size, URL, language, platform, ' +
      'cookies enabled, online status. Useful for layout (viewport), locale (language), or offline warnings.',
    annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: true },
    outputSchema: okStatus,
    parameters: { type: 'object', properties: { why: whyParam }, required: ['why'] },
    handler: async () => {
      const env = {
        userAgent: navigator.userAgent,
        viewport: { width: window.innerWidth, height: window.innerHeight },
        url: window.location.href,
        language: navigator.language,
        platform: navigator.platform,
        cookiesEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
      };
      return {
        status: 'ok',
        ...env,
        hint: env.onLine
          ? 'Browser is online; external fetches will work.'
          : 'Browser is offline — warn the user before tools that hit the network.',
      };
    },
  });

  // ── getSelection — current page selection (agent sees "browser_getSelection") ──
  methods.registerTool({
    name: 'getSelection',
    category: 'browser-capability',
    disclosureGroup: 'browser',
    description:
      'Read the text the user has currently highlighted on the page. Returns { text, length, hint }. ' +
      'Useful when the user says "summarise this" without re-pasting.',
    annotations: { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: true },
    outputSchema: okStatus,
    parameters: { type: 'object', properties: { why: whyParam }, required: ['why'] },
    handler: async () => {
      const text = window.getSelection()?.toString() || '';
      return {
        status: 'ok',
        text,
        length: text.length,
        hint: text.length === 0 ? 'Nothing is currently selected on the page.' : 'Use `text` as context.',
      };
    },
  });

  // ── Canvas tools (render_* + canvas_info + canvas_manage) ────────
  for (const tool of createCanvasToolDefinitions()) {
    methods.registerTool({
      name: tool.name,
      handler: tool.handler,
      description: tool.description,
      parameters: tool.parameters,
      fireAndForget: tool.fireAndForget,
      annotations: tool.annotations,
      outputSchema: tool.outputSchema,
    } satisfies RegisteredTool);
  }

  // Curated catalog copy. Description is shown verbatim in the agent's
  // tool-discovery catalog row (≤ CATALOG_ROW_DESCRIPTION_MAX = 80 chars);
  // notes surface after group activation (≤ CATALOG_GROUP_NOTES_MAX = 2 KB).
  methods.setGroupMeta({
    description:
      'UI canvas (render/update/inventory/clear cards, tables, charts, forms) + dialogs',
    notes:
      'Canvas model\n' +
      '------------\n' +
      'The user has up to 5 named canvases in the right-hand panel. Default is "main"; ' +
      'omit `canvas_id` unless targeting another surface. Every render_* tool returns ' +
      '`component_id` and `canvas_id` — remember them to update or remove. If you forget, ' +
      'call `canvas_info(action:"list_components")` to recover.\n\n' +
      'Component lifecycle (all render_* tools):\n' +
      '  - Create: render_* with `component_id` omitted. Fresh ID returned. status="rendered".\n' +
      '  - Update: render_* with the existing `component_id`. Replaced in place; idempotent. status="updated".\n' +
      '  - Remove: canvas_manage(action:"remove_component", component_id:…). Idempotent — safe even if gone.\n\n' +
      'Canvas-level control:\n' +
      '  - canvas_info (read-only): list | list_components (with limit/offset).\n' +
      '  - canvas_manage (destructive): set_active | clear | close | clear_all | toggle_panel | remove_component.\n' +
      'Create a canvas implicitly — render any component with a new `canvas_id`.\n\n' +
      'Dialog + info surface\n' +
      '---------------------\n' +
      '  - browser_confirmAction / browser_promptUser interrupt the user — sparingly, only for ' +
      'consequential decisions or a single short answer. Prefer render_form for multi-field input.\n' +
      '  - browser_getClipboard / browser_getBrowserInfo / browser_getSelection are read-only.\n\n' +
      'Principles\n' +
      '----------\n' +
      'Every tool takes a required `why` field (short intent sentence — audit log).\n' +
      'Every response carries a `hint` pointing to the next reasonable action.\n' +
      'Errors are structured { status:"error", code, message, hint, context? } — not thrown.\n' +
      'render_chart validates x_key / y_key against data[0] pre-flight; use `available_keys` ' +
      'in the error context to recover.\n\n' +
      'All tools require an active browser session — RPC calls fail with REQUEST_TIMEOUT ' +
      'once the user navigates away.',
  });

  return methods;
}
